/**
 * Server-Sent Events (SSE) Client
 * Handles POST-based SSE streams for chat
 *
 * Used for:
 * - /chat endpoint with streaming responses
 * - Real-time thinking steps
 * - Progressive response chunks
 */

import { qaRequestIdHeaders } from './qa-telemetry'

export interface SseEvent<T = unknown> {
  type: string
  data: T
}

/**
 * QA timing marks (Phase 1 harness, #5521). Emit a `performance.mark` at the SSE
 * stream lifecycle points the `ui-test-agent` sse-stall detector keys on:
 * `xavior:qa:sse-open`, `xavior:qa:sse-heartbeat` (one per heartbeat frame), and
 * `xavior:qa:sse-done` (stream complete/error). Every mark's `detail` carries a
 * per-stream `streamId` correlator so CONCURRENT streams (e.g. the onboarding
 * greeting + a chat turn) can be told apart — without it their heartbeats interleave
 * and corrupt the detector's per-stream cadence.
 *
 * These are INERT in production — a `performance.mark` is a no-op ring entry no code
 * reads unless the QA tap's PerformanceObserver is installed — and fully guarded
 * (feature-detected + try/catch), so they can never alter the stream's behavior or
 * throw into the chat loop.
 */
let qaSseStreamSeq = 0
/** Streams currently emitting marks — heartbeat marks are cleared once ALL have ended. */
const qaActiveSseStreams = new Set<number>()
const QA_SSE_MARK_NAMES = ['xavior:qa:sse-open', 'xavior:qa:sse-heartbeat', 'xavior:qa:sse-done']

function qaSseMark(name: string, detail?: Record<string, unknown>): void {
  try {
    if (typeof performance === 'undefined' || typeof performance.mark !== 'function') return
    // The `detail` option is ignored by engines that predate it; the try/catch
    // also covers the (rare) engine that rejects the options argument outright.
    performance.mark(name, detail ? { detail } : undefined)
  } catch {
    // Telemetry must never break the stream.
  }
}

/**
 * Bound the mark ring: heartbeat marks accumulate for the life of a stream, so once a
 * stream ends we clear the QA marks — but ONLY when no other stream is still active, so
 * a concurrent live stream's heartbeats survive. The tap's observer harvests marks as
 * they are created, so clearing after `sse-done` never loses an event.
 */
function qaEndSseStream(streamId: number): void {
  qaActiveSseStreams.delete(streamId)
  if (qaActiveSseStreams.size > 0) return
  try {
    if (typeof performance === 'undefined' || typeof performance.clearMarks !== 'function') return
    for (const name of QA_SSE_MARK_NAMES) performance.clearMarks(name)
  } catch {
    // Best-effort cleanup only.
  }
}

/**
 * Open a POST-based SSE stream
 * Returns an async generator that yields events
 *
 * @example
 * ```ts
 * for await (const event of postSse('/chat', { message: 'Hello' }, headers)) {
 *   if (event.type === 'thinking') {
 *     console.log('AI is thinking:', event.data)
 *   }
 * }
 * ```
 */
export async function* postSse<T = unknown>(
  url: string,
  body: unknown,
  headers: Record<string, string>,
  signal?: AbortSignal,
): AsyncGenerator<SseEvent<T>, void, unknown> {
  let response: Response

  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'text/event-stream',
        'Content-Type': 'application/json',
        ...headers,
        // QA-only correlation id, minted fresh per fetch attempt (the gateway
        // fallback below is a second attempt, so it gets its own id).
        ...qaRequestIdHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify(body),
      signal,
    })
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return
    }
    // Network error (connection refused) - try x-agent directly
    if (url.includes('localhost:9080')) {
      const fallbackUrl = url.replace('localhost:9080', 'localhost:8300')
      console.warn('[SSE] Gateway failed, trying x-agent directly:', fallbackUrl)

      response = await fetch(fallbackUrl, {
        method: 'POST',
        headers: {
          'Accept': 'text/event-stream',
          'Content-Type': 'application/json',
          ...headers,
          // Fresh id for this fallback attempt (distinct from the primary above).
          ...qaRequestIdHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify(body),
        signal,
      })
    } else {
      throw error
    }
  }

  if (!response.ok || !response.body) {
    // Parse error body for structured info (e.g., 429 rate limit details)
    let errorMessage = `SSE request failed: ${response.status} ${response.statusText}`
    let resetAt: string | undefined
    let errorCode: string | undefined
    const retryAfterHeader = response.headers.get('Retry-After')
    const retryAfterSec = retryAfterHeader ? parseInt(retryAfterHeader, 10) : undefined
    try {
      const body = await response.json()
      if (body?.message) errorMessage = body.message
      else if (typeof body?.error === 'string') errorMessage = body.error
      if (typeof body?.resetAt === 'string') resetAt = body.resetAt
      if (typeof body?.error === 'string') errorCode = body.error
    } catch {
      // non-JSON body
    }
    const err = new Error(errorMessage) as Error & {
      status?: number
      resetAt?: string
      errorCode?: string
      retryAfterSec?: number
    }
    err.status = response.status
    if (resetAt) err.resetAt = resetAt
    if (errorCode) err.errorCode = errorCode
    if (retryAfterSec && !isNaN(retryAfterSec)) err.retryAfterSec = retryAfterSec
    throw err
  }

  const reader = response.body.getReader()
  // Stream physically opened (response OK, body readable) — the sse-stall detector
  // anchors its cadence window on this mark. `streamId` correlates this stream's marks
  // so a concurrent stream can't corrupt the cadence. (#5521)
  const streamId = ++qaSseStreamSeq
  qaActiveSseStreams.add(streamId)
  qaSseMark('xavior:qa:sse-open', { url, streamId })
  const decoder = new TextDecoder('utf-8')
  let buffer = ''
  // Reason stamped on the terminal `sse-done` mark. Refined to `error`/`aborted`
  // by the branches below (and the catch) before the finally emits it.
  let doneReason: 'complete' | 'error' | 'aborted' = 'complete'

  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })

      // Process complete messages (delimited by \n\n)
      let boundaryIndex
      while ((boundaryIndex = buffer.indexOf('\n\n')) !== -1) {
        const rawMessage = buffer.slice(0, boundaryIndex)
        buffer = buffer.slice(boundaryIndex + 2)

        const lines = rawMessage.split('\n')
        let eventType = 'message'
        const dataLines: string[] = []

        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim()
          } else if (line.startsWith('data:')) {
            dataLines.push(line.slice(5).trim())
          }
        }

        const dataString = dataLines.join('')

        // One mark per heartbeat frame received (#5521) — this is the cadence the
        // sse-stall detector measures. Fires independently of whether the frame
        // is also yielded below (a heartbeat with no data payload is not).
        if (eventType === 'heartbeat') {
          qaSseMark('xavior:qa:sse-heartbeat', { streamId })
        }

        // Keep-alive heartbeats ARE yielded (#5194): the chat stream loop uses
        // them to keep its liveness timestamp fresh and to re-assert the
        // thinking indicator across the long silent gaps of a page/report
        // build (research phase, between-section pauses). Swallowing them here
        // made an alive stream indistinguishable from a dead one, so the
        // foreground-reload handler aborted healthy long builds. Consumers
        // treat unknown event types as no-ops, so propagating is safe.

        // Structured in-stream error frames: throw a typed Error so callers
        // get the same shape as pre-stream HTTP errors (status, resetAt,
        // errorCode). This branch owns the throw; upstream for-await loops
        // should let the thrown error propagate to their outer try/catch.
        if (eventType === 'error') {
          doneReason = 'error'
          let payload: {
            message?: string
            status?: number
            resetAt?: string
            errorCode?: string
          } | null = null
          try {
            payload = JSON.parse(dataString)
          } catch {
            throw new Error(dataString || 'Chat error')
          }
          const err = new Error(payload?.message || 'Chat error') as Error & {
            status?: number
            resetAt?: string
            errorCode?: string
          }
          if (payload?.status) err.status = payload.status
          if (payload?.resetAt) err.resetAt = payload.resetAt
          if (payload?.errorCode) err.errorCode = payload.errorCode
          throw err
        }

        if (dataString) {
          try {
            const data = JSON.parse(dataString) as T
            yield { type: eventType, data }
          } catch {
            // Non-JSON data is ignored; never log the raw payload — even the
            // SyntaxError is withheld, since V8 embeds the source text in
            // error.message.
            console.warn('[SSE] Failed to parse event data (non-JSON payload ignored)')
          }
        }
      }
    }
  } catch (err) {
    // Observe-and-rethrow: refine the terminal mark's reason without changing how
    // the error propagates (the finally still runs, the caller still receives it).
    if (err instanceof Error && err.name === 'AbortError') doneReason = 'aborted'
    else if (doneReason === 'complete') doneReason = 'error'
    throw err
  } finally {
    reader.releaseLock()
    qaSseMark('xavior:qa:sse-done', { reason: doneReason, streamId })
    // Retire this stream; clears the mark ring once no stream is still active (#5521).
    qaEndSseStream(streamId)
  }
}

/**
 * Open a GET-based SSE stream.
 *
 * Mirrors {@link postSse} for endpoints whose stream is keyed by URL query
 * params rather than a request body (e.g. the onboarding smart-step stream
 * `GET /onboarding/steps/:id/stream?recipeKey=…`). Unlike `postSse` it sends no
 * body and does not attempt the gateway→x-agent fallback (the smart-step stream
 * lives on the API gateway, not x-agent). Frame parsing and in-stream
 * `event: error` handling are identical. Unlike `postSse` (which yields
 * heartbeats so the chat loop can track stream liveness, #5194), this GET
 * variant still suppresses `event: heartbeat` frames — its consumers have no
 * liveness tracking.
 */
export async function* getSse<T = unknown>(
  url: string,
  headers: Record<string, string>,
  signal?: AbortSignal,
): AsyncGenerator<SseEvent<T>, void, unknown> {
  let response: Response
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'text/event-stream',
        ...headers,
        // QA-only correlation id joining this stream open to its API log line.
        ...qaRequestIdHeaders(),
      },
      credentials: 'include',
      signal,
    })
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') return
    throw error
  }

  if (!response.ok || !response.body) {
    let errorMessage = `SSE request failed: ${response.status} ${response.statusText}`
    try {
      const body = await response.json()
      if (body?.message) errorMessage = body.message
      else if (typeof body?.error === 'string') errorMessage = body.error
    } catch {
      // non-JSON body
    }
    const err = new Error(errorMessage) as Error & { status?: number }
    err.status = response.status
    throw err
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''

  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      let boundaryIndex
      while ((boundaryIndex = buffer.indexOf('\n\n')) !== -1) {
        const rawMessage = buffer.slice(0, boundaryIndex)
        buffer = buffer.slice(boundaryIndex + 2)

        const lines = rawMessage.split('\n')
        let eventType = 'message'
        const dataLines: string[] = []
        for (const line of lines) {
          if (line.startsWith('event:')) eventType = line.slice(6).trim()
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
        }
        const dataString = dataLines.join('')

        if (eventType === 'heartbeat') continue

        if (dataString) {
          try {
            const data = JSON.parse(dataString) as T
            yield { type: eventType, data }
          } catch {
            // Never log the raw payload (or the SyntaxError, which embeds it).
            console.warn('[SSE] Failed to parse event data (non-JSON payload ignored)')
          }
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

